import React from 'react';
import { parseChain, levelFromStatus } from '../lib/levels';

// Os priebehu dokladu v detaile: Prijaté → úrovne reťazca → Účtovníčka.
// Odpovedá na otázku "kde to je a prečo to ešte nie je u účtovníčky".
interface TimelineDoc {
  created_at: string;
  status: string;
  required_level?: string;
  dispatched_at?: string | null;
  auto_approved?: boolean;
  email_message_id?: string | null;
  submitted_by_email?: string;
  waiting_on?: string[];
  waiting_since?: string | null;
}
interface TimelineEntry { action: string; level: number; user_name: string; created_at: string }

type State = 'done' | 'active' | 'pending' | 'bad';
interface Step { key: string; title: string; state: State; line1?: string; line2?: string }

const when = (s?: string | null) => s ? new Date(s).toLocaleDateString('sk-SK', { day: 'numeric', month: 'numeric' }) : '';
const daysSince = (s?: string | null) => {
  if (!s) return null;
  const a = new Date(s); a.setHours(0, 0, 0, 0);
  const b = new Date(); b.setHours(0, 0, 0, 0);
  const n = Math.max(0, Math.round((b.getTime() - a.getTime()) / 86400000));
  return n === 0 ? 'od dnes' : n === 1 ? '1 deň' : n < 5 ? `${n} dni` : `${n} dní`;
};

export default function ApprovalTimeline({ doc, history, skipReason }: { doc: TimelineDoc; history: TimelineEntry[]; skipReason?: string | null }) {
  const current = levelFromStatus(doc.status);
  // Staršie doklady bez reťazca: ukáž aspoň úrovne z histórie a aktuálnu.
  const chain: number[] = doc.required_level
    ? parseChain(doc.required_level)
    : Array.from(new Set([...history.filter(h => h.action === 'approved' && h.level > 0).map(h => h.level), ...(current ? [current] : [])])).sort((a, b) => a - b);
  const lastOf = (level: number, action: string) =>
    [...history].reverse().find(h => h.level === level && h.action === action);
  const stopped = doc.status === 'rejected' || doc.status === 'returned';
  const stopEntry = stopped ? [...history].reverse().find(h => h.action === 'rejected' || h.action === 'returned') : undefined;

  const steps: Step[] = [{
    key: 'in', title: 'Prijaté', state: 'done',
    line1: when(doc.created_at),
    line2: doc.email_message_id ? 'e-mailom' : doc.submitted_by_email === 'peppol' ? 'e-faktúra' : 'nahraté',
  }];

  for (const lvl of chain) {
    const ok = lastOf(lvl, 'approved');
    let st: Step;
    if (doc.auto_approved) {
      st = { key: `l${lvl}`, title: `L${lvl}`, state: 'done', line1: 'automaticky' };
    } else if (stopEntry && stopEntry.level === lvl) {
      st = { key: `l${lvl}`, title: `L${lvl}`, state: 'bad', line1: doc.status === 'rejected' ? 'zamietnuté' : 'vrátené na opravu', line2: `${stopEntry.user_name} · ${when(stopEntry.created_at)}` };
    } else if (current === lvl) {
      st = { key: `l${lvl}`, title: `L${lvl}`, state: 'active', line1: (doc.waiting_on || []).join(', ') || 'čaká na schválenie', line2: daysSince(doc.waiting_since) ? `leží ${daysSince(doc.waiting_since)}` : undefined };
    } else if (ok && (doc.status === 'approved' || current === null || lvl < (current as number))) {
      st = { key: `l${lvl}`, title: `L${lvl}`, state: 'done', line1: ok.user_name, line2: when(ok.created_at) };
    } else {
      st = { key: `l${lvl}`, title: `L${lvl}`, state: 'pending', line1: 'ešte nie' };
    }
    steps.push(st);
  }

  steps.push(
    doc.status === 'approved' && doc.dispatched_at
      ? { key: 'acc', title: 'Účtovníčka', state: 'done', line1: 'odoslané', line2: when(doc.dispatched_at) }
      : doc.status === 'approved'
        ? { key: 'acc', title: 'Účtovníčka', state: 'active', line1: 'čaká na odoslanie' }
        : { key: 'acc', title: 'Účtovníčka', state: 'pending', line1: stopped ? 'neodošle sa' : 'po schválení' }
  );

  const skipped = chain.length > 0 && chain[0] > 1 && !doc.auto_approved;

  return (
    <div className="tl-wrap">
      <div className="tl">
        {steps.map((s, i) => (
          <div key={s.key} className={`tl-step is-${s.state}`}>
            <div className="tl-rail">
              <span className="tl-dot">{s.state === 'done' ? '✓' : s.state === 'bad' ? '✕' : i + 1}</span>
              {i < steps.length - 1 && <span className="tl-line" />}
            </div>
            <div className="tl-title">{s.title}</div>
            {s.line1 && <div className="tl-text">{s.line1}</div>}
            {s.line2 && <div className="tl-text is-dim">{s.line2}</div>}
          </div>
        ))}
      </div>
      {skipped && (
        <div className="tl-note">
          Doklad išiel rovno na L{chain[0]}, nižšia úroveň ho neschvaľuje.{skipReason ? ` ${skipReason}` : ''}
        </div>
      )}
    </div>
  );
}
